/**
 * qicons — lucide icons that speak MUI's prop language (Phase 9 icon convergence).
 *
 * THE PROBLEM: the portal had 2,269 icon call sites and 58% of them pass `sx=` — MUI's
 * SvgIcon accepts it, lucide does not. Rewriting 1,300+ call sites is the wrong shape of
 * fix. THE MOVE: `styled()` grants ANY component the `sx` prop, so this module exports
 * every icon the portal uses as a lucide glyph wrapped to accept the MUI icon surface:
 *
 *   sx           — full support (styled). `sx={{ fontSize: 16 }}` sizes the icon, because
 *                  the wrapper renders at width/height 1em and lets CSS font-size drive
 *                  size, exactly like MUI's SvgIcon. CSS beats lucide's width/height
 *                  attributes, so the glyph follows.
 *   fontSize     — "small" | "medium" | "large" | "inherit" -> 16 / 20 / 28 / inherit px.
 *                  (Slightly under MUI's 20/24/35: lucide fills its viewBox where MUI
 *                  pads ~17%, so parity sizes run smaller — the fleet register.)
 *   color        — MUI palette names (success/error/primary/warning/info/secondary/
 *                  action/disabled) resolve to theme tokens; anything else passes
 *                  through as a CSS color. Default inherits currentColor.
 *
 * Exports use the MUI ICON NAMES (Add, Edit, Delete, …), so converting a file is an
 * import-line rewrite and every call site stays byte-identical. The codemod is
 * scripts/icons_qify_phase9.py.
 */
import * as React from 'react';
import { styled } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import type { LucideIcon, LucideProps } from 'lucide-react';
import {
  CloudOff as LCloudOff,
  LayoutDashboard,
  Image as LImage,
  Move,
  ExternalLink as LLaunch,
  Activity, AlertCircle, AlertOctagon, AlertTriangle, AppWindow, Archive as LArchive,
  ArrowDown, ArrowLeft, ArrowLeftRight, ArrowRight, ArrowUp, ArrowUpDown, Award, Ban,
  BadgeCheck, BarChart3, Bell, BellRing, BookOpen, Bookmark as LBookmark, BoxSelect, Bug,
  Building2, Calculator, Calendar, CalendarDays, CalendarRange, Check as LCheck,
  CheckCircle as LCheckCircleOutline, CheckCircle2, ChevronDown, ChevronLeft as LChevronLeft,
  ChevronRight as LChevronRight, ChevronUp, Circle, CircleDollarSign, ClipboardCheck,
  ClipboardList, Clock, Cloud as LCloud, CloudUpload as LCloudUpload, Code as LCode,
  Columns3, Construction as LConstruction, Copy, Database, DollarSign, Download as LDownload,
  ExternalLink, Eye, EyeOff, FileText, FilePlus, FileUp, Files, Filter, FilterX, Flag as LFlag,
  FlaskConical, FolderCheck, FolderOpen as LFolderOpen, Gauge, Gavel as LGavel, GitCompare,
  Globe, GraduationCap, Grid3x3, GripVertical, HelpCircle, History as LHistory, Home as LHome,
  Hourglass, Info as LInfo, Landmark, Layers as LLayers, Library, Lightbulb as LLightbulb,
  LineChart, Link as LLink, List as LList, ListChecks, ListFilter, ListPlus, ListTodo,
  ListVideo, LocateFixed, Lock as LLock, LockOpen as LLockOpen, LogOut, Mail as LMail,
  Maximize, Menu as LMenu, Merge as LMerge, MessageSquare, Minimize as LMinimize, Minus,
  MinusCircle, Moon, MoreHorizontal, MoreVertical, MoveRight, Network, Package, Palette as
  LPalette, PanelTop, Pause as LPause, PauseCircle as LPauseCircle, Pencil, Percent as
  LPercent, PieChart as LPieChart, Play, PlayCircle as LPlayCircle, Plus, Printer, Receipt as
  LReceipt, RefreshCw, RotateCcw, Save as LSave, ScatterChart, Scissors, Search as LSearch,
  SearchX, Send as LSend, Server, Settings as LSettings, Share2, Shield as LShield,
  ShieldCheck, ShoppingCart as LShoppingCart, Sigma, SkipBack, SkipForward,
  SlidersHorizontal, Snowflake, Split, Square, Star as LStar, StickyNote, Sun, Table as
  LTable, Table2, Tag, ThumbsDown, ThumbsUp, Timer as LTimer, Trash2, TrendingDown as
  LTrendingDown, TrendingUp as LTrendingUp, Undo2, Unlink, Upload as LUpload, User, UserCheck,
  UserCog, UserMinus, UserPlus, Users, UsersRound, Wallet, Waypoints, Wifi as LWifi, WifiOff
  as LWifiOff, Wrench, X, XCircle,
} from 'lucide-react';

const SIZE: Record<string, number | string> = { small: 16, medium: 20, large: 28, inherit: 'inherit' };
const PALETTE: Record<string, string> = {
  primary: 'primary.main', secondary: 'secondary.main', success: 'success.main',
  error: 'error.main', warning: 'warning.main', info: 'info.main',
  action: 'action.active', disabled: 'action.disabled', inherit: 'inherit',
};

export interface QIconProps extends Omit<LucideProps, 'color' | 'size' | 'ref'> {
  sx?: SxProps<Theme>;
  fontSize?: 'small' | 'medium' | 'large' | 'inherit' | number;
  color?: string;
  size?: number | string;
  /** MUI SvgIcon a11y prop — becomes aria-label. */
  titleAccess?: string;
}

function muiify(Icon: LucideIcon): React.FC<QIconProps> {
  // width/height 1em + font-size = MUI SvgIcon's sizing model; CSS beats the svg attrs.
  const Styled = styled(Icon)({ width: '1em', height: '1em' });
  const C: React.FC<QIconProps> = ({ fontSize = 'medium', color, size, sx, titleAccess, ...rest }) => {
    const fs = size ?? (typeof fontSize === 'number' ? fontSize : SIZE[fontSize] ?? 20);
    const colorSx = color ? { color: PALETTE[color] ?? color } : {};
    return (
      <Styled
        aria-label={titleAccess}
        sx={[{ fontSize: fs, flexShrink: 0 }, colorSx, ...(Array.isArray(sx) ? sx : [sx])] as SxProps<Theme>}
        {...rest}
      />
    );
  };
  C.displayName = `QIcon(${Icon.displayName ?? 'Icon'})`;
  return C;
}

/* Exports keyed by the MUI icon name each replaces — call sites stay byte-identical. */
export const AcUnit = muiify(Snowflake);
export const AccessTime = muiify(Clock);
export const AccountBalance = muiify(Landmark);
export const AccountBalanceWallet = muiify(Wallet);
export const AccountTree = muiify(Network);
export const Add = muiify(Plus);
export const AddCircleOutline = muiify(Plus);
export const AltRoute = muiify(Split);
export const Analytics = muiify(BarChart3);
export const Archive = muiify(LArchive);
export const ArrowBack = muiify(ArrowLeft);
export const ArrowDownward = muiify(ArrowDown);
export const ArrowForward = muiify(ArrowRight);
export const ArrowUpward = muiify(ArrowUp);
export const Article = muiify(FileText);
export const Assessment = muiify(BarChart3);
export const Assignment = muiify(ClipboardList);
export const AttachMoney = muiify(DollarSign);
export const AutoGraph = muiify(LTrendingUp);
export const Autorenew = muiify(RefreshCw);
export const BarChart = muiify(BarChart3);
export const Block = muiify(Ban);
export const Bookmark = muiify(LBookmark);
export const Brightness4 = muiify(Moon);
export const Brightness7 = muiify(Sun);
export const BugReport = muiify(Bug);
export const Build = muiify(Wrench);
export const Business = muiify(Building2);
export const Calculate = muiify(Calculator);
export const CalculateOutlined = muiify(Calculator);
export const CalendarMonth = muiify(Calendar);
export const CalendarToday = muiify(CalendarDays);
export const CallSplit = muiify(Split);
export const Cancel = muiify(XCircle);
export const Check = muiify(LCheck);
export const CheckCircle = muiify(CheckCircle2);
export const CheckCircleOutline = muiify(LCheckCircleOutline);
export const ChevronLeft = muiify(LChevronLeft);
export const ChevronRight = muiify(LChevronRight);
export const Clear = muiify(X);
export const Close = muiify(X);
export const Cloud = muiify(LCloud);
export const CloudUpload = muiify(LCloudUpload);
export const CloudOff = muiify(LCloudOff);
export const Code = muiify(LCode);
export const Comment = muiify(MessageSquare);
export const Compare = muiify(GitCompare);
export const CompareArrows = muiify(ArrowLeftRight);
export const Construction = muiify(LConstruction);
export const ContentCopy = muiify(Copy);
export const FileCopy = muiify(Copy);
export const Image = muiify(LImage);
export const OpenWith = muiify(Move);
export const ContentCut = muiify(Scissors);
export const CropSquare = muiify(Square);
export const CurrencyExchange = muiify(CircleDollarSign);
export const Dashboard = muiify(LayoutDashboard);
export const DateRange = muiify(CalendarRange);
export const Delete = muiify(Trash2);
export const DeleteForever = muiify(Trash2);
export const DeleteOutline = muiify(Trash2);
export const Description = muiify(FileText);
export const Dns = muiify(Server);
export const Done = muiify(LCheck);
export const Download = muiify(LDownload);
export const DragIndicator = muiify(GripVertical);
export const Edit = muiify(Pencil);
export const EditNote = muiify(Pencil);
export const Email = muiify(LMail);
export const Error = muiify(AlertCircle);
export const ErrorOutline = muiify(AlertCircle);
export const Event = muiify(Calendar);
export const EventNote = muiify(CalendarDays);
export const ExpandLess = muiify(ChevronUp);
export const ExpandMore = muiify(ChevronDown);
export const FactCheck = muiify(ClipboardCheck);
export const FileDownload = muiify(LDownload);
export const FileUpload = muiify(LUpload);
export const FilterList = muiify(ListFilter);
export const FilterListOff = muiify(FilterX);
export const FilterNone = muiify(LLayers);
export const Flag = muiify(LFlag);
export const FolderOpen = muiify(LFolderOpen);
export const Fullscreen = muiify(Maximize);
export const FullscreenExit = muiify(LMinimize);
export const Functions = muiify(Sigma);
export const Gavel = muiify(LGavel);
export const GpsFixed = muiify(LocateFixed);
export const GridOn = muiify(Grid3x3);
export const Group = muiify(Users);
export const GroupWork = muiify(UsersRound);
export const Help = muiify(HelpCircle);
export const HelpOutline = muiify(HelpCircle);
export const History = muiify(LHistory);
export const Home = muiify(LHome);
export const HourglassBottom = muiify(Hourglass);
export const HourglassEmpty = muiify(Hourglass);
export const HowToReg = muiify(UserCheck);
export const Hub = muiify(Waypoints);
export const Info = muiify(LInfo);
export const InfoOutlined = muiify(LInfo);
export const Inventory = muiify(Package);
export const KeyboardArrowDown = muiify(ChevronDown);
export const KeyboardArrowRight = muiify(LChevronRight);
export const Launch = muiify(ExternalLink);
export const KeyboardArrowUp = muiify(ChevronUp);
export const Label = muiify(Tag);
export const Language = muiify(Globe);
export const Layers = muiify(LLayers);
export const LibraryBooks = muiify(Library);
export const Lightbulb = muiify(LLightbulb);
export const Link = muiify(LLink);
export const LinkOff = muiify(Unlink);
export const ListAlt = muiify(LList);
export const Lock = muiify(LLock);
export const LockOpen = muiify(LLockOpen);
export const Logout = muiify(LogOut);
export const Mail = muiify(LMail);
export const ManageAccounts = muiify(UserCog);
export const Menu = muiify(LMenu);
export const MenuBook = muiify(BookOpen);
export const Merge = muiify(LMerge);
export const Message = muiify(MessageSquare);
export const Minimize = muiify(Minus);
export const MoreHoriz = muiify(MoreHorizontal);
export const MoreVert = muiify(MoreVertical);
export const NavigateBefore = muiify(LChevronLeft);
export const NavigateNext = muiify(LChevronRight);
export const Notes = muiify(StickyNote);
export const Notifications = muiify(Bell);
export const NotificationsActive = muiify(BellRing);
export const OpenInNew = muiify(ExternalLink);
export const Pages = muiify(Files);
export const Palette = muiify(LPalette);
export const Pause = muiify(LPause);
export const PauseCircle = muiify(LPauseCircle);
export const People = muiify(Users);
export const Percent = muiify(LPercent);
export const Person = muiify(User);
export const PersonAdd = muiify(UserPlus);
export const PersonRemove = muiify(UserMinus);
export const PictureAsPdf = muiify(FileText);
export const PieChart = muiify(LPieChart);
export const PivotTableChart = muiify(Table2);
export const PlayArrow = muiify(Play);
export const PlayCircle = muiify(LPlayCircle);
export const PlayCircleOutline = muiify(LPlayCircle);
export const PlaylistAdd = muiify(ListPlus);
export const PlaylistAddCheck = muiify(ListChecks);
export const PlaylistPlay = muiify(ListVideo);
export const Policy = muiify(LShield);
export const PostAdd = muiify(FilePlus);
export const Preview = muiify(Eye);
export const Print = muiify(Printer);
export const Public = muiify(Globe);
export const Publish = muiify(LUpload);
export const RadioButtonUnchecked = muiify(Circle);
export const Receipt = muiify(LReceipt);
export const Refresh = muiify(RefreshCw);
export const Remove = muiify(Minus);
export const RemoveCircle = muiify(MinusCircle);
export const RemoveCircleOutline = muiify(MinusCircle);
export const Replay = muiify(RotateCcw);
export const Report = muiify(AlertOctagon);
export const ReportProblem = muiify(AlertTriangle);
export const Restore = muiify(RotateCcw);
export const Rule = muiify(ListTodo);
export const RuleFolder = muiify(FolderCheck);
export const Save = muiify(LSave);
export const ScatterPlot = muiify(ScatterChart);
export const Schedule = muiify(Clock);
export const School = muiify(GraduationCap);
export const Science = muiify(FlaskConical);
export const Search = muiify(LSearch);
export const SearchOff = muiify(SearchX);
export const Security = muiify(LShield);
export const SelectAll = muiify(BoxSelect);
export const Sell = muiify(Tag);
export const Send = muiify(LSend);
export const Settings = muiify(LSettings);
export const Share = muiify(Share2);
export const Shield = muiify(LShield);
export const ShoppingCart = muiify(LShoppingCart);
export const ShowChart = muiify(LineChart);
export const SkipNext = muiify(SkipForward);
export const SkipPrevious = muiify(SkipBack);
export const Speed = muiify(Gauge);
export const Star = muiify(LStar);
export const StarBorder = muiify(LStar);
export const Stop = muiify(Square);
export const Storage = muiify(Database);
export const SwapHoriz = muiify(ArrowLeftRight);
export const SwapVert = muiify(ArrowUpDown);
export const Tab = muiify(PanelTop);
export const TableChart = muiify(LTable);
export const ThumbDown = muiify(ThumbsDown);
export const ThumbUp = muiify(ThumbsUp);
export const Timeline = muiify(Activity);
export const Timer = muiify(LTimer);
export const TrendingDown = muiify(LTrendingDown);
export const TrendingFlat = muiify(MoveRight);
export const TrendingUp = muiify(LTrendingUp);
export const Tune = muiify(SlidersHorizontal);
export const Undo = muiify(Undo2);
export const Upload = muiify(LUpload);
export const UploadFile = muiify(FileUp);
export const Verified = muiify(BadgeCheck);
export const VerifiedUser = muiify(ShieldCheck);
export const ViewColumn = muiify(Columns3);
export const ViewList = muiify(LList);
export const ViewModule = muiify(AppWindow);
export const Visibility = muiify(Eye);
export const VisibilityOff = muiify(EyeOff);
export const Warning = muiify(AlertTriangle);
export const WarningAmber = muiify(AlertTriangle);
export const WatchLater = muiify(Clock);
export const WaterfallChart = muiify(BarChart3);
export const Wifi = muiify(LWifi);
export const WifiOff = muiify(LWifiOff);
export const WorkspacePremium = muiify(Award);
